import { useCallback } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useSignInWithSocialCodeMutation } from '~/libs/gql/mutations/eat/signInWithSocialCodeMutation.generated';
import { EatSessionDocument } from '~/libs/gql/queries/eat/eatSessionQuery.generated';
import { useSocialAuth } from '~/utils/useSocialAuth';
import { usePopmenuConfig } from '../usePopmenuConfig';
import { sanitizeReturnTo, withReturnTo } from '../useReturnTo';

export type SocialProvider = 'apple' | 'facebook' | 'google';

const PROVIDER_NAMES: Record<SocialProvider, string> = {
  apple: 'Apple',
  facebook: 'Facebook',
  google: 'Google',
};

// useSocialAuth only sends the current path, so the page to return to is kept here across the popup or redirect
const RETURN_TO_STORAGE_KEY = 'eat.signIn.returnTo';

const storeReturnTo = (returnTo: string) => {
  try {
    window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, returnTo);
  } catch {
    // Private browsing can block storage; the redirect fallback then returns to the home page
  }
};

export const takeStoredReturnTo = () => {
  try {
    const returnTo = window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    return sanitizeReturnTo(returnTo);
  } catch {
    return sanitizeReturnTo(null);
  }
};

const codeVariables = (provider: SocialProvider, token: string) => {
  switch (provider) {
    case 'apple':
      return { appleCode: token };
    case 'facebook':
      return { facebookCode: token };
    case 'google':
      return { googleCode: token };
    default:
      throw new Error(`Unknown social provider: ${String(provider)}`);
  }
};

const genericErrorMessage = (intl: IntlShape) => intl.formatMessage({ defaultMessage: "We couldn't sign you in. Please try again." });

const errorMessage = (intl: IntlShape, error: unknown, provider: SocialProvider) => {
  const message = error instanceof Error ? error.message : '';
  if (message.includes('sign_in.facebook.invalid_permissions')) {
    return intl.formatMessage({ defaultMessage: "We couldn't get your email from Facebook. Allow email access, or sign in another way." });
  }
  // Raised for an address linked to a different account with the provider, and by FacebookService for root users
  if (message.includes('sign_up.email_taken')) {
    return intl.formatMessage({ defaultMessage: "This email can't sign in with {provider}." }, { provider: PROVIDER_NAMES[provider] });
  }
  return genericErrorMessage(intl);
};

export const useSocialSignIn = () => {
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { signInUrl } = usePopmenuConfig();
  const { kickoffExternalAuthFlow } = useSocialAuth({ signInUrl });
  const [signInWithSocialCode, { loading }] = useSignInWithSocialCodeMutation({
    update: (cache, { data }) => {
      if (!data) return;

      cache.writeQuery({ data: { currentSession: data.signInWithSocialCode.session }, query: EatSessionDocument });
    },
  });

  const signInWithToken = useCallback(async (provider: SocialProvider, token: string, returnTo: string) => {
    try {
      const { data } = await signInWithSocialCode({ variables: codeVariables(provider, token) });
      // New accounts get the welcome page first, the same as after a first email link
      history.replace(data?.signInWithSocialCode.isNewAccount ? withReturnTo('/welcome', returnTo) : returnTo);
    } catch (error) {
      enqueueSnackbar(errorMessage(intl, error, provider), { variant: 'error' });
    }
  }, [enqueueSnackbar, history, intl, signInWithSocialCode]);

  const startSocialSignIn = useCallback((provider: SocialProvider, returnTo: string) => {
    storeReturnTo(returnTo);
    void kickoffExternalAuthFlow({
      onAuthSuccess: ({ data }) => {
        takeStoredReturnTo();
        void signInWithToken(provider, data.token, returnTo);
      },
      onError: () => {
        enqueueSnackbar(genericErrorMessage(intl), { variant: 'error' });
      },
      // In-app browsers often block popups. Signing in through this tab brings the token back in the URL hash,
      // and this tab's sessionStorage still has returnTo.
      onPopupBlocked: (socialAuthUrl) => {
        window.location.assign(socialAuthUrl);
      },
      social: provider,
    });
  }, [enqueueSnackbar, intl, kickoffExternalAuthFlow, signInWithToken]);

  return { loading, signInWithToken, startSocialSignIn };
};
